import { useQuery, useMutation } from "@apollo/client";
import { useEffect, useRef, useState } from "react";
import queries from "../queries";
import common, {
  calculateSubtotal,
  calculateTotal,
  getCurrCurrency,
  isMobileScreen,
  setPageTitle,
  showLoading,
} from "../common";
import Swal from "sweetalert2";
import { ImportMainJs } from "../helper/ImportScript";
import tokenizeCard from "../helper/Tokenization";
import { useSessionStorage } from "../context/SessionStorageContext";
import * as LocalCartHelper from "../helper/LocalCartHelper";

// Authorize.Net configuration
const AUTHORIZE_NET_CONFIG = {
  // Change these based on environment
  apiLoginId:
    process.env.REACT_APP_AUTHORIZE_NET_API_LOGIN_ID || "YOUR_API_LOGIN_ID",
  clientKey:
    process.env.REACT_APP_AUTHORIZE_NET_PUBLIC_CLIENT_KEY ||
    "YOUR_PUBLIC_CLIENT_KEY",
  environment: process.env.REACT_APP_AUTHORIZE_NET_ENVIRONMENT || "sandbox", // "sandbox" or "production"
};

function Checkout({ dataSelf, loading, error, refetch }) {
  const isCustomPaymentGateway = false;
  const useAuthorizeNet = process.env.REACT_APP_USE_AUTHORIZE_NET === "true"; // Feature flag

  // const countryRef = useRef();
  const stateRef = useRef();
  const cityRef = useRef();
  const acceptJsLoaded = useRef(false);

  setPageTitle("Secure Checkout | Complete Your Hemp & Kratom Order");
  const [cart, setCart] = useState([]);
  const { sessionData, updateSessionStorage, isUserLoggedIn, localCart } =
    useSessionStorage();

  useEffect(() => {
    if (dataSelf != null) {
      ImportMainJs();
      setCart(dataSelf?.getSelf?.cart);
      setFName(dataSelf?.getSelf?.fname);
      setLName(dataSelf?.getSelf?.lname);
      setEmail(dataSelf?.getSelf?.email);
      window.scrollTo(0, 0);
    }
  }, [dataSelf]);

  useEffect(() => {
    setCart(localCart);
  }, [localCart]);

  // Load Accept.js script dynamically
  useEffect(() => {
    if (useAuthorizeNet && !acceptJsLoaded.current) {
      const scriptUrl =
        AUTHORIZE_NET_CONFIG.environment === "production"
          ? "https://js.authorize.net/v3/AcceptUI.js"
          : "https://jstest.authorize.net/v3/AcceptUI.js";

      const script = document.createElement("script");
      script.src = scriptUrl;
      script.async = true;
      script.charset = "utf-8";
      script.onload = () => {
        console.log("Accept.js loaded successfully");
        acceptJsLoaded.current = true;
      };
      script.onerror = () => {
        console.error("Failed to load Accept.js");
      };
      document.body.appendChild(script);

      return () => {
        // Cleanup script on unmount
        if (script.parentNode) {
          document.body.removeChild(script);
        }
      };
    }
  }, [useAuthorizeNet]);

  const [createCheckoutSession] = useMutation(
    queries.general.CREATE_CHECKOUT_SESSION
  );
  const [placeOrderByAuthToken] = useMutation(
    queries.general.PLACE_ORDER_BY_AUTH_TOKEN
  );
  const [placeOrderBySquare] = useMutation(
    queries.general.PLACE_ORDER_BY_SQUARE
  );
  const [placeOrderWithAuthorizeNet] = useMutation(
    queries.general.PLACE_ORDER_WITH_AUTHORIZE_NET
  );
  const [verifyPromoCode] = useMutation(queries.general.VERIFY_PROMO_CODE);

  const [country, setCountry] = useState("United States");
  const [state, setState] = useState();
  const [city, setCity] = useState();
  const [fname, setFName] = useState();
  const [email, setEmail] = useState();
  const [lname, setLName] = useState();
  const [promoCode, setPromoCode] = useState();
  const [line1, setLine1] = useState();
  const [cardNumber, setCardNumber] = useState();
  const [cvv, setCVV] = useState();
  const [expiryDate, setExpiryDate] = useState();
  const [postal_code, setPostalCode] = useState();
  const [phone, setPhone] = useState();
  const [note, setNote] = useState();
  const [promoResult, setPromoResult] = useState(null);

  const {
    data: dataCountries,
    loading: loadingCountries,
    error: errorCountries,
  } = useQuery(queries.general.GET_COUNTRIES);
  const {
    data: dataStates,
    loading: loadingStates,
    error: errorStates,
  } = useQuery(queries.general.GET_STATES, {
    variables: {
      country: country,
    },
  });
  const {
    data: dataCities,
    loading: loadingCities,
    error: errorCities,
  } = useQuery(queries.general.GET_CITIES, {
    variables: {
      state: state,
    },
  });

  async function handlePlaceOrder(e) {
    e.preventDefault();
    if (validateForm()) {
      showLoading("Checkout in progress...");
      let products = cart.map((item) => {
        let temp = item;
        if (temp.options != null && temp.options != []) {
          temp.options.forEach((ele) => {
            delete ele.__typename;
          });
        }
        return {
          _id: item.product_id,
          type: item.product.type,
          subtype: item.product.subtype,
          qty: item.qty,
          options: temp.options,
        };
      });
      const checkoutArgs = {
        checkoutSessionArgs: {
          fname,
          lname,
          address: {
            line1,
            state,
            city,
            country,
            postal_code,
          },
          note: note,
          products: products,
          currency: "USD",
          phone: phone,
          promoCode: promoCode,
        },
      };
      try {
        const { data } = await createCheckoutSession({
          variables: checkoutArgs,
        });
        window.location.href = data?.createCheckoutSession;
      } catch (error) {
        if (typeof error == "string")
          error = {
            message: error,
          };
        Swal.fire({
          icon: "error",
          title: error.message,
        });
      }
    }
  }

  async function handlePlaceOrderByAuthToken(e) {
    e.preventDefault();
    if (validateForm()) {
      showLoading("Checkout in progress...");

      let products = cart.map((item) => {
        let temp = item;
        if (temp.options != null && temp.options != []) {
          temp.options.forEach((ele) => {
            delete ele.__typename;
          });
        }
        return {
          _id: item.product_id,
          type: item.product.type,
          subtype: item.product.subtype,
          qty: item.qty,
          options: temp.options,
        };
      });
      const checkoutSessionArgs = {
        fname,
        lname,
        address: {
          line1,
          state: state.toUpperCase(),
          city,
          country,
          postal_code,
        },
        note: note,
        products: products,
        currency: "USD",
        phone: phone,
        promoCode: promoCode,
      };

      try {
        const cardToken = await tokenizeCard(cardNumber, expiryDate, cvv);
        const { data } = await placeOrderByAuthToken({
          variables: {
            checkoutSessionArgs: checkoutSessionArgs,
            token: cardToken,
            email: email,
          },
        });
        if (data?.placeOrderByAuthToken) {
          Swal.fire({
            icon: "success",
            title: "Order placed successfully",
          }).then(() => {
            LocalCartHelper.clearLocalCart(updateSessionStorage);
            window.location.href = "/my-orders";
          });
        } else {
          throw new Error("Could not place the order at the moment.");
        }
      } catch (error) {
        if (typeof error == "string")
          error = {
            message: error,
          };
        Swal.fire({
          icon: "error",
          title: error.message,
        });
      }
    }
  }

  async function handleVerifyPromoCode(e) {
    e.preventDefault();
    try {
      setPromoResult(null);
      const { data } = await verifyPromoCode({
        variables: {
          code: promoCode,
          localCartArgs: LocalCartHelper.getInitialCart().map((item) => {
            return {
              product_id: item.product_id,
              qty: item.qty,
              options: item.options,
              isBogoFree: item.isBogoFree,
            };
          }),
        },
      });
      if (data?.verifyPromotionCode?.valid) {
        Swal.fire({
          icon: "success",
          title: "Promo code applied successfully",
        });
        setPromoResult(data?.verifyPromotionCode);
      } else {
        Swal.fire({
          icon: "error",
          title: data?.verifyPromotionCode?.message || "Invalid promo code",
        });
      }
    } catch (error) {
      if (typeof error == "string")
        error = {
          message: error,
        };
      Swal.fire({
        icon: "error",
        title: error.message,
      });
    }
  }

  async function handlePlaceOrderBySquare(e) {
    e.preventDefault();
    if (validateForm()) {
      showLoading("Checkout in progress...");

      let products = cart.map((item) => {
        let temp = item;
        if (temp.options != null && temp.options != []) {
          temp.options.forEach((ele) => {
            delete ele.__typename;
          });
        }
        return {
          _id: item._id,
          product_id: item.product_id,
          type: item.product.type,
          subtype: item.product.subtype,
          qty: item.qty,
          options: temp.options,
          isBogoFree: item.isBogoFree,
        };
      });
      const checkoutSessionArgs = {
        fname,
        lname,
        address: {
          line1,
          state: state.toUpperCase(),
          city,
          country,
          postal_code,
        },
        note: note,
        products: products,
        currency: "USD",
        phone: phone,
        promoCode: promoCode,
      };

      try {
        const { data } = await placeOrderBySquare({
          variables: {
            checkoutSessionArgs: checkoutSessionArgs,
            email: email,
          },
        });
        if (data?.placeOrderBySquare?.url) {
          window.location.href = data?.placeOrderBySquare?.url;
        } else {
          throw new Error("Could not place the order at the moment.");
        }
      } catch (error) {
        if (typeof error == "string")
          error = {
            message: error,
          };
        Swal.fire({
          icon: "error",
          title: error.message,
        });
      }
    }
  }

  /**
   * Handle Authorize.Net payment using Accept.js
   * Opens the AcceptUI modal for secure card entry
   */
  async function handlePlaceOrderWithAuthorizeNet(e) {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    if (!acceptJsLoaded.current) {
      Swal.fire({
        icon: "error",
        title:
          "Payment system not ready. Please refresh the page and try again.",
      });
      return;
    }

    // Prepare order data
    let products = cart.map((item) => {
      let temp = item;
      if (temp.options != null && temp.options != []) {
        temp.options.forEach((ele) => {
          delete ele.__typename;
        });
      }
      return {
        _id: item._id,
        product_id: item.product_id,
        type: item.product.type,
        subtype: item.product.subtype,
        qty: item.qty,
        options: temp.options,
        isBogoFree: item.isBogoFree,
      };
    });

    const checkoutSessionArgs = {
      fname,
      lname,
      address: {
        line1,
        state: state.toUpperCase(),
        city,
        country,
        postal_code,
      },
      note: note,
      products: products,
      currency: "USD",
      phone: phone,
      promoCode: promoCode,
    };

    // AcceptUI callback - receives payment nonce
    window.responseHandler = async function (response) {
      if (response.messages.resultCode === "Error") {
        let errorMsg = "";
        for (let i = 0; i < response.messages.message.length; i++) {
          errorMsg += response.messages.message[i].text + "\n";
        }
        Swal.fire({
          icon: "error",
          title: "Payment Error",
          text: errorMsg,
        });
        return;
      }

      // Payment nonce received from Accept.js
      const opaqueData = {
        dataDescriptor: response.opaqueData.dataDescriptor,
        dataValue: response.opaqueData.dataValue,
      };

      showLoading("Processing payment...");

      try {
        const { data } = await placeOrderWithAuthorizeNet({
          variables: {
            checkoutSessionArgs: checkoutSessionArgs,
            opaqueData: opaqueData,
            email: email,
          },
        });

        if (data?.placeOrderWithAuthorizeNet) {
          Swal.fire({
            icon: "success",
            title: "Order placed successfully!",
            text: "You will receive a confirmation email shortly.",
          }).then(() => {
            LocalCartHelper.clearLocalCart(updateSessionStorage);
            window.location.href = "/my-orders";
          });
        } else {
          throw new Error("Could not place the order at the moment.");
        }
      } catch (error) {
        console.error("Payment error:", error);
        Swal.fire({
          icon: "error",
          title: "Payment Failed",
          text:
            error.message ||
            "Please verify your payment information and try again.",
        });
      }
    };

    // Trigger AcceptUI modal
    const authData = {
      clientKey: AUTHORIZE_NET_CONFIG.clientKey,
      apiLoginID: AUTHORIZE_NET_CONFIG.apiLoginId,
    };

    const cardData = {
      fullName: `${fname} ${lname}`,
      zip: postal_code,
    };

    // Call AcceptUI to open payment modal
    if (window.Accept) {
      window.Accept.dispatchData(
        {
          authData: authData,
          cardData: cardData,
        },
        window.responseHandler
      );
    } else {
      Swal.fire({
        icon: "error",
        title: "Payment system not available. Please refresh and try again.",
      });
    }
  }

  function validateForm() {
    const errors = [];
    if (email == null || email.trim() === "") {
      errors.push("Please enter email");
    } else if (!common.isValidEmail(email)) {
      errors.push("Please enter valid email");
    }
    if (country == null || country.trim() == "") {
      errors.push("Please select country");
    }
    if (state == null || state.trim() == "") {
      errors.push("Please select state");
    }
    if (city == null || city.trim() == "") {
      errors.push("Please select city");
    }
    if (fname == null || fname.trim() == "") {
      errors.push("Please enter first name");
    }
    if (lname == null || lname.trim() == "") {
      errors.push("Please enter last name");
    }
    if (line1 == null || line1.trim() == "") {
      errors.push("Please enter street address");
    }
    if (postal_code == null || postal_code.trim() == "") {
      errors.push("Please enter postal code");
    }
    if (
      phone != null &&
      (phone.trim() == "" ||
        isNaN(phone?.replaceAll("-", "")) ||
        phone?.trim()?.length != 12)
    ) {
      errors.push("Please enter valid phone");
    }
    // if (cardNumber == null || cardNumber.trim() === "") {
    //   errors.push("Please enter Card Number");
    // }
    // if (expiryDate == null || expiryDate.trim() === "") {
    //   errors.push("Please enter Card Expiry Date");
    // }
    // if (cvv == null || cvv.trim() === "") {
    //   errors.push("Please enter Card CVV");
    // }
    if (errors.length > 0) {
      Swal.fire({
        icon: "error",
        title: "Please correct these errors in the form before submitting",
        html: errors.join("<br/>"),
      });
      return false;
    }
    return true;
  }

  useEffect(() => {
    // const observerCountry = new MutationObserver((mutationsList) => {
    //   for (const mutation of mutationsList) {
    //     if (
    //       mutation.type === "childList" ||
    //       mutation.type === "characterData"
    //     ) {
    //       // Content of the div has changed
    //       const country = mutation?.target?.title;
    //       if (country != null) {
    //         setCountry(country);
    //       }
    //     }
    //   }
    // });

    // observerCountry.observe(countryRef.current, {
    //   childList: true,
    //   subtree: true,
    //   characterData: true,
    // });

    const observerState = new MutationObserver((mutationsList) => {
      for (const mutation of mutationsList) {
        if (
          mutation.type === "childList" ||
          mutation.type === "characterData"
        ) {
          // Content of the div has changed
          const state = mutation?.target?.title;
          if (state != null) {
            setState(state);
          }
        }
      }
    });

    observerState.observe(stateRef.current, {
      childList: true,
      subtree: true,
      characterData: true,
    });

    const observerCity = new MutationObserver((mutationsList) => {
      for (const mutation of mutationsList) {
        if (
          mutation.type === "childList" ||
          mutation.type === "characterData"
        ) {
          // Content of the div has changed
          const city = mutation?.target?.title;
          if (city != null) {
            setCity(city);
          }
        }
      }
    });

    observerCity.observe(cityRef.current, {
      childList: true,
      subtree: true,
      characterData: true,
    });

    return () => {
      // observerCountry.disconnect();
      observerState.disconnect();
      observerCity.disconnect();
    };
  }, []);

  useEffect(() => {
    if (dataCountries?.getCountries != null) {
      setCountry("United States");
    }
  }, [dataCountries]);

  return (
    <>
      <section
        className="breadcrumbs-custom"
        data-preset='{"title":"Breadcrumbs","category":"header","reload":false,"id":"breadcrumbs"}'
      >
        <div className="breadcrumbs-custom-container">
          <ul className="breadcrumbs-custom-path">
            <li>
              <a href="/">Home</a>
            </li>
            <li className="active">Checkout</li>
          </ul>
        </div>
      </section>
      <section
        className={isMobileScreen() ? "bg-white" : "section-sm bg-white"}
      >
        <div className="container">
          <h2 className="text-center">Checkout</h2>
          <div className="row justify-content-center">
            <div className="checkout-box col-lg-6 mb-4">
              <div className="row row-20">
                <div className="col-md-6">
                  <div className="form-wrap">
                    <label
                      className="form-label form-label-outside"
                      for="checkout-name"
                    >
                      First name *
                    </label>
                    <input
                      className="form-input"
                      id="checkout-name"
                      data-constraints="@Required"
                      type="text"
                      name="name"
                      value={fname}
                      onChange={(e) => setFName(e.target.value)}
                      placeholder="Jane"
                      autoComplete="given-name"
                    />
                  </div>
                </div>
                <div className="col-md-6">
                  <div className="form-wrap">
                    <label
                      className="form-label form-label-outside"
                      for="checkout-name-last"
                    >
                      Last name *
                    </label>
                    <input
                      className="form-input"
                      id="checkout-name-last"
                      data-constraints="@Required"
                      type="text"
                      name="name"
                      value={lname}
                      onChange={(e) => setLName(e.target.value)}
                      placeholder="Doe"
                      autoComplete="family-name"
                    />
                  </div>
                </div>
                <div className="col-md-12">
                  <div className="form-wrap">
                    <label
                      className="form-label form-label-outside"
                      for="checkout-address"
                    >
                      Street address *
                    </label>
                    <input
                      className="form-input"
                      id="checkout-address"
                      data-constraints="@Required"
                      type="text"
                      name="name"
                      value={line1}
                      onChange={(e) => setLine1(e.target.value)}
                      placeholder="123 Main Street"
                    />
                  </div>
                </div>
                <div className="col-md-5">
                  <div className="form-wrap" ref={cityRef}>
                    <label
                      className="form-label form-label-outside"
                      for="checkout-city"
                    >
                      City *
                    </label>
                    <input
                      className="form-input"
                      id="checkout-city"
                      data-constraints="@Required"
                      type="text"
                      name="city"
                      value={city}
                      onChange={(e) => setCity(e.target.value)}
                      placeholder="New York City"
                    />
                    {/* <select id="checkout-city">
                    {dataCities?.getCities?.map((c) => {
                      return (
                        <option selected={city == c.city_name}>
                          {c.city_name}
                        </option>
                      );
                    })}
                  </select> */}
                  </div>
                </div>
                <div className="col-md-2">
                  <div className="form-wrap" ref={stateRef}>
                    <label
                      className="form-label form-label-outside"
                      for="checkout-state"
                    >
                      State *
                    </label>
                    <input
                      className="form-input"
                      id="checkout-state"
                      data-constraints="@Required"
                      style={{ textTransform: "uppercase" }}
                      type="text"
                      name="state"
                      value={state}
                      maxLength={2}
                      autoCapitalize="characters"
                      onChange={(e) => setState(e.target.value)}
                      placeholder="NY"
                    />
                    {/* <select id="checkout-state">
                    {dataStates?.getStates?.map((s) => {
                      return (
                        <option selected={state == s.state_name}>
                          {s.state_name}
                        </option>
                      );
                    })}
                  </select> */}
                  </div>
                </div>
                <div className="col-md-5">
                  <div className="form-wrap">
                    <label
                      className="form-label form-label-outside"
                      for="checkout-postcode"
                    >
                      Postcode *
                    </label>
                    <input
                      className="form-input"
                      id="checkout-postcode"
                      data-constraints="@Required"
                      type="text"
                      name="name"
                      value={postal_code}
                      maxLength={5}
                      onChange={(e) =>
                        setPostalCode(e.target.value.replace(/\D/g, ""))
                      }
                      placeholder="10007"
                    />
                  </div>
                </div>
                <div className="col-md-12">
                  <div className="form-wrap">
                    <label
                      className="form-label form-label-outside"
                      for="checkout-phone"
                    >
                      Phone (optional)
                    </label>
                    <input
                      className="form-input"
                      id="checkout-phone"
                      type="phone"
                      name="name"
                      value={phone}
                      placeholder="234-555-1234"
                      onChange={(event) => {
                        let input = event.target.value;
                        input = input.replace(/\D/g, ""); // remove non-numeric characters

                        // restrict input to maximum of 12 digits
                        if (input.length > 10) {
                          input = input.slice(0, 10);
                        }

                        let formattedInput = "";

                        if (input.length > 3 && input.length <= 6) {
                          formattedInput =
                            input.slice(0, 3) + "-" + input.slice(3);
                        } else if (input.length > 6) {
                          formattedInput =
                            input.slice(0, 3) +
                            "-" +
                            input.slice(3, 6) +
                            "-" +
                            input.slice(6, 12);
                        } else {
                          formattedInput = input;
                        }

                        setPhone(formattedInput);
                      }}
                    />
                  </div>
                </div>
                <div className="col-md-12">
                  <div className="form-wrap">
                    <label
                      className="form-label form-label-outside"
                      for="checkout-info"
                    >
                      Order notes (optional)
                    </label>
                    <textarea
                      className="form-input"
                      id="checkout-info"
                      value={note}
                      onChange={(e) => setNote(e.target.value)}
                      placeholder="e.g. Special notes for delivery"
                    ></textarea>
                  </div>
                </div>
              </div>

              {/* <div className="form-wrap" ref={countryRef}>
                  <label
                    className="form-label form-label-outside"
                    for="checkout-country"
                  >
                    Country *
                  </label>
                  <select id="checkout-country" value={country}>
                    {dataCountries?.getCountries?.map((c) => {
                      return <option>{c.country_name}</option>;
                    })}
                  </select>
                </div> */}
            </div>
            <div className="checkout-box col-lg-6">
              <div className="row row-20">
                <div className="col-md-12">
                  <div className="form-wrap">
                    <label
                      className="form-label form-label-outside"
                      for="checkout-name"
                    >
                      Email *
                    </label>
                    <input
                      className="form-input"
                      id="checkout-email"
                      type="email"
                      name="email"
                      data-constraints="@Email @Required"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      placeholder="jane.doe@gmail.com"
                    />
                  </div>
                </div>
                {!isMobileScreen() && (
                  <>
                    <div className="col-md-10">
                      <div className="form-wrap">
                        <label
                          className="form-label form-label-outside"
                          for="checkout-promocode"
                        >
                          Promo Code
                        </label>
                        <input
                          className="form-input"
                          id="checkout-promocode"
                          type="text"
                          name="promocode"
                          value={promoCode}
                          onChange={(e) => setPromoCode(e.target.value)}
                          onKeyDown={(e) => {
                            if (e.key === "Enter") {
                              handleVerifyPromoCode(e);
                            }
                          }}
                          placeholder="Enter promo code"
                        />
                      </div>
                    </div>
                    <div className="col-md-2">
                      <button
                        className="btn btn-link mt-4 ml-n5"
                        onClick={handleVerifyPromoCode}
                      >
                        Apply
                      </button>
                    </div>
                  </>
                )}
                {isMobileScreen() && (
                  <>
                    <div className="col-10">
                      <div className="form-wrap">
                        <label
                          className="form-label form-label-outside"
                          for="checkout-promocode"
                        >
                          Promo Code
                        </label>
                        <input
                          className="form-input"
                          id="checkout-promocode"
                          type="text"
                          name="promocode"
                          value={promoCode}
                          onChange={(e) => setPromoCode(e.target.value)}
                          onKeyDown={(e) => {
                            if (e.key === "Enter") {
                              handleVerifyPromoCode(e);
                            }
                          }}
                          placeholder="Enter promo code"
                        />
                      </div>
                    </div>
                    <div className="col-2">
                      <button
                        className="btn btn-link mt-4 ml-n5"
                        onClick={verifyPromoCode}
                      >
                        Apply
                      </button>
                    </div>
                  </>
                )}
                {isCustomPaymentGateway && (
                  <div>
                    <div className="col-md-12">
                      <div className="form-wrap">
                        <label
                          className="form-label form-label-outside"
                          for="checkout-address"
                        >
                          Card Number *
                        </label>
                        <input
                          className="form-input"
                          id="checkout-cardnumber"
                          data-constraints="@Required"
                          type="text"
                          name="cardnumber"
                          value={cardNumber}
                          onChange={(e) => {
                            const input = e.target.value.replace(/\D/g, "");
                            const formattedCardNumber =
                              input.match(/.{1,4}/g)?.join(" ") || "";
                            setCardNumber(formattedCardNumber);
                          }}
                          maxLength={19}
                          placeholder="1234 1234 1234 1234"
                        />
                      </div>
                    </div>
                    <div className="col-md-6">
                      <div className="form-wrap">
                        <label
                          className="form-label form-label-outside"
                          for="checkout-address"
                        >
                          Expiry Date *
                        </label>
                        <input
                          className="form-input"
                          id="checkout-expirydate"
                          data-constraints="@Required"
                          type="text"
                          name="expirydate"
                          value={expiryDate}
                          onChange={(e) => {
                            const input = e.target.value.replace(/\D/g, ""); // Remove non-digit characters
                            let formattedExpDate = input;

                            if (input.length >= 2) {
                              let month = parseInt(input.slice(0, 2), 10);

                              if (month > 12) {
                                month = 12; // Set to December if month is greater than 12
                              }

                              formattedExpDate = month
                                .toString()
                                .padStart(2, "0"); // Format month

                              if (input.length > 2) {
                                const year = input.slice(2, 4);
                                formattedExpDate += `/${year}`;
                              }
                            }

                            setExpiryDate(formattedExpDate);
                          }}
                          maxLength={5}
                          placeholder="MM/YY"
                        />
                      </div>
                    </div>
                    <div className="col-md-6">
                      <div className="form-wrap">
                        <label
                          className="form-label form-label-outside"
                          for="checkout-address"
                        >
                          CVC *
                        </label>
                        <input
                          className="form-input"
                          id="checkout-cvc"
                          data-constraints="@Required"
                          type="text"
                          name="cvc"
                          value={cvv}
                          onChange={(e) => {
                            setCVV(e.target.value.replace(/\D/g, ""));
                          }}
                          maxLength={4}
                          placeholder="1234"
                        />
                      </div>
                    </div>
                    <div className="col-md-12">
                      * We prioritize your security. We do not store your card
                      information; it is only used to securely process your
                      order.
                    </div>
                  </div>
                )}
              </div>
              <div className="table-custom-responsive">
                <table className="table-order">
                  <thead>
                    <tr>
                      <th></th>
                      <th>Product</th>
                      <th></th>
                      <th>Subtotal</th>
                    </tr>
                  </thead>
                  <tbody>
                    {cart.map((cartItem) => {
                      return (
                        <tr>
                          <td>
                            <figure className="cart-item-col cart-item-col-figure">
                              <a
                                className="link-image rounded"
                                href={`/product/${encodeURIComponent(
                                  cartItem?.product?.slug
                                )}`}
                              >
                                <img
                                  src={
                                    cartItem?.options?.[0]?.image?.length > 0
                                      ? cartItem?.options?.[0]?.image
                                      : cartItem?.product?.image?.[0]
                                  }
                                  alt=""
                                  width="260"
                                  height="312"
                                />
                              </a>
                            </figure>
                          </td>
                          <td>
                            {cartItem?.product.name}{" "}
                            {cartItem?.options?.length > 0 ? " - " : ""}
                            {cartItem?.options?.map((o) => o.value + " ")}
                            {cartItem.isBogoFree && (
                              <span className="text-primary"> (FREE)</span>
                            )}
                          </td>
                          <td>x {cartItem?.qty}</td>
                          <td>
                            <div
                              className={
                                cartItem.isBogoFree ? "text-strike" : ""
                              }
                            >
                              {getCurrCurrency()}
                              {calculateSubtotal([
                                cart.find(
                                  (i) => cartItem.product_id === i.product_id
                                ),
                              ])}
                            </div>
                            {cartItem.isBogoFree && (
                              <div>
                                <span className="cart-subtitle font-weight-bold text-primary">
                                  FREE
                                </span>
                              </div>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                  <tfoot>
                    <tr>
                      <td></td>
                      <td></td>
                      <td className="font-weight-medium pb-0">Subtotal:</td>
                      <td className="font-weight-bold text-primary pb-0">
                        {getCurrCurrency()}
                        {calculateSubtotal(cart)}
                      </td>
                    </tr>
                    {promoResult?.discountAmount > 0 && (
                      <tr>
                        <td></td>
                        <td></td>
                        <td className="font-weight-medium pb-0 py-0">
                          discount:
                        </td>
                        <td className="font-weight-bold text-primary pb-0 py-0">
                          -{getCurrCurrency()}
                          {promoResult?.discountAmount}
                        </td>
                      </tr>
                    )}
                    <tr>
                      <td></td>
                      <td></td>
                      <td className="font-weight-medium py-0">Total:</td>
                      <td className="font-weight-bold text-primary py-0">
                        {getCurrCurrency()}
                        {calculateTotal(cart, promoResult)}
                      </td>
                    </tr>
                    <tr>
                      <td style={{ fontSize: 14 }} colSpan={4}>
                        * Taxes & Shipping will be calculated before payment.
                      </td>
                    </tr>
                  </tfoot>
                </table>
              </div>
              <div className="text-right mt-5">
                <a
                  className="btn btn-primary"
                  href="javascript:void(0);"
                  onClick={
                    useAuthorizeNet
                      ? handlePlaceOrderWithAuthorizeNet
                      : handlePlaceOrderBySquare
                  }
                >
                  Continue to Payment
                </a>
                {useAuthorizeNet && (
                  <p className="text-muted mt-2" style={{ fontSize: "12px" }}>
                    <i className="fa fa-lock"></i> Secure payment powered by
                    Authorize.Net
                  </p>
                )}
              </div>
            </div>
          </div>
        </div>
      </section>
    </>
  );
}

export default Checkout;
